import { useState, useEffect } from 'react';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { MdEdit, MdDelete, MdClose, MdAdd, MdVisibility, MdPhotoCamera } from 'react-icons/md';
import Pagination from '../components/Pagination';
import { formatDate } from '../utils/format';

export default function Admins() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // 'create' or 'edit'
  const [formData, setFormData] = useState({ name: '', email: '', password: '', role: 'reporter', image: '' });
  const [uploadingImage, setUploadingImage] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [previewImage, setPreviewImage] = useState(null); // image lightbox popup

  // Reporter View Stats & Articles State
  const [viewModal, setViewModal] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [reporterStats, setReporterStats] = useState(null);
  const [daySummary, setDaySummary] = useState([]);
  const [reporterArticles, setReporterArticles] = useState([]);
  const [articlesPagination, setArticlesPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [reporterArticlesLimit, setReporterArticlesLimit] = useState(10);

  const fetchReporterStats = async (reporterId) => {
    try {
      const res = await api.get(`/admin/admins/${reporterId}/stats`);
      setReporterStats(res.data.data);
    } catch (err) {
      toast.error('Failed to fetch reporter statistics');
    }
  };

  const fetchReporterArticles = async (reporterId) => {
    setStatsLoading(true);
    try {
      const res = await api.get('/admin/articles', {
        params: {
          admin_id: reporterId,
          status: 'published',
          limit: 10000
        }
      });
      const allArticles = res.data.data;

      const dateGroups = {};
      allArticles.forEach(art => {
        if (!art.published_at) return;
        const d = new Date(art.published_at);
        const normalizedDate = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        const timeKey = normalizedDate.getTime();
        dateGroups[timeKey] = (dateGroups[timeKey] || 0) + 1;
      });

      const sortedGroups = Object.keys(dateGroups)
        .map(time => ({
          time: Number(time),
          dateStr: formatDate(new Date(Number(time))),
          count: dateGroups[time]
        }))
        .sort((a, b) => b.time - a.time);

      setDaySummary(sortedGroups);
      setReporterArticles(sortedGroups.slice(0, reporterArticlesLimit));
      setArticlesPagination({ page: 1, pages: Math.ceil(sortedGroups.length / reporterArticlesLimit) || 1, total: sortedGroups.length });
    } catch (err) {
      toast.error('Failed to fetch reporter articles');
    } finally {
      setStatsLoading(false);
    }
  };

  const handleView = (admin) => {
    setViewModal(admin);
    setReporterStats(null);
    setReporterArticles([]);
    setDaySummary([]);
    setArticlesPagination({ page: 1, pages: 1, total: 0 });
    fetchReporterStats(admin.id);
    fetchReporterArticles(admin.id);
  };

  const handlePageChange = (newPage) => {
    setArticlesPagination(prev => ({ ...prev, page: newPage }));
    setReporterArticles(daySummary.slice((newPage - 1) * reporterArticlesLimit, newPage * reporterArticlesLimit));
  };

  const handleReporterLimitChange = (newLimit) => {
    setReporterArticlesLimit(newLimit);
    setArticlesPagination({ page: 1, pages: Math.ceil(daySummary.length / newLimit) || 1, total: daySummary.length });
    setReporterArticles(daySummary.slice(0, newLimit));
  };

  const fetchAdmins = async () => {
    try {
      const res = await api.get('/admin/admins');
      setAdmins(res.data.data);
    } catch (err) {
      toast.error('Failed to fetch admins');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const openCreate = () => {
    setFormData({ name: '', email: '', password: '', role: 'reporter', image: '' });
    setModal('create');
  };

  const openEdit = (admin) => {
    setFormData({ id: admin.id, name: admin.name, email: admin.email, password: '', role: admin.role, image: admin.image || '' });
    setModal('edit');
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/admin/upload/avatar', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data?.data?.url;
      if (url) {
        setFormData(prev => ({ ...prev, image: url }));
        toast.success('Photo uploaded');
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to upload photo');
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (modal === 'create') {
        if (!formData.password) {
          return toast.error('Password is required for new accounts');
        }
        await api.post('/admin/admins', formData);
        toast.success('Admin/Reporter created');
      } else {
        await api.put(`/admin/admins/${formData.id}`, formData);
        toast.success('Account updated');
      }
      setModal(null);
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Action failed');
    }
  };

  const [deleteModal, setDeleteModal] = useState(null);

  const confirmDelete = async () => {
    if (!deleteModal) return;
    setDeletingId(deleteModal.id);
    try {
      await api.delete(`/admin/admins/${deleteModal.id}`);
      toast.success('Account deleted');
      setDeleteModal(null);
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Delete failed');
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) return <div className="page-loader">Loading...</div>;

  return (
    <div className="page">
      <div className="page-header">
        <h2>Manage Admins & Reporters</h2>
        <button className="btn btn-primary" onClick={openCreate}><MdAdd /> Add New</button>
      </div>

      <div className="table-wrap mt-md">
        <table className="table">
          <thead>
            <tr>
              <th>Profile / Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {admins.map(admin => (
              <tr key={admin.id}>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div 
                      style={{
                        width: 38,
                        height: 38,
                        borderRadius: 10,
                        background: admin.role === 'admin' ? 'rgba(239, 35, 60, 0.12)' : 'rgba(59, 130, 246, 0.12)',
                        color: admin.role === 'admin' ? '#EF233C' : '#3B82F6',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 700,
                        fontSize: 15,
                        overflow: 'hidden',
                        flexShrink: 0,
                        border: '1px solid rgba(0,0,0,0.06)',
                        cursor: admin.image ? 'pointer' : 'default',
                        transition: 'transform 0.15s ease',
                      }}
                      onClick={() => admin.image && setPreviewImage(admin)}
                      title={admin.image ? 'Click to view full photo' : ''}
                    >
                      {admin.image ? (
                        <img src={admin.image} alt={admin.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        admin.name?.charAt(0) || 'A'
                      )}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, color: 'var(--text)' }}>{admin.name}</div>
                    </div>
                  </div>
                </td>
                <td>{admin.email}</td>
                <td>
                  <span className={`badge ${admin.role === 'admin' ? 'badge-live' : 'badge-category'}`}>
                    {admin.role.toUpperCase()}
                  </span>
                </td>
                <td>
                  <div className="action-btns">
                    <button className="btn btn-sm btn-ghost" onClick={() => handleView(admin)} title="View Details"><MdVisibility /></button>
                    <button className="btn btn-sm btn-ghost" onClick={() => openEdit(admin)} title="Edit"><MdEdit /></button>
                    <button className="btn btn-sm btn-danger" onClick={() => setDeleteModal(admin)} disabled={deletingId === admin.id} title="Delete"><MdDelete /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{modal === 'create' ? 'Create Account' : 'Edit Account'}</h3>
              <button className="modal-close" onClick={() => setModal(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <form className="modal-form" onSubmit={handleSubmit}>
                {/* Profile Photo Upload */}
                <div className="form-group" style={{ textAlign: 'center', marginBottom: 18 }}>
                  <label style={{ display: 'block', marginBottom: 8 }}>Profile Photo</label>
                  <div style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                    <div style={{
                      width: 72,
                      height: 72,
                      borderRadius: 16,
                      background: 'var(--bg)',
                      border: '2px dashed #CBD5E1',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      overflow: 'hidden',
                      position: 'relative',
                    }}>
                      {formData.image ? (
                        <img src={formData.image} alt="Avatar preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <span style={{ fontSize: 24, fontWeight: 700, color: 'var(--text-secondary)' }}>
                          {formData.name?.charAt(0) || <MdPhotoCamera size={28} />}
                        </span>
                      )}
                    </div>

                    <div style={{ display: 'flex', gap: 8 }}>
                      <label className="btn btn-sm btn-ghost" style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                        <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleImageUpload} disabled={uploadingImage} />
                        <MdPhotoCamera size={14} />
                        <span>{uploadingImage ? 'Uploading...' : (formData.image ? 'Change' : 'Upload Photo')}</span>
                      </label>
                      {formData.image && (
                        <button type="button" className="btn btn-sm btn-ghost text-danger" onClick={() => setFormData(p => ({ ...p, image: '' }))}>
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label>Name</label>
                  <input type="text" value={formData.name} onChange={e => setFormData({ ...formData, name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Role</label>
                  <select value={formData.role} onChange={e => setFormData({ ...formData, role: e.target.value })} required>
                    <option value="admin">Admin</option>
                    <option value="reporter">Reporter</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>{modal === 'edit' ? 'Password (leave blank to keep current)' : 'Password'}</label>
                  <input type="password" value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })} minLength={6} />
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-ghost" onClick={() => setModal(null)}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={uploadingImage}>Save</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {viewModal && (
        <div className="modal-overlay" onClick={() => setViewModal(null)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div 
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 12,
                    background: viewModal.role === 'admin' ? 'rgba(239, 35, 60, 0.12)' : 'rgba(59, 130, 246, 0.12)',
                    color: viewModal.role === 'admin' ? '#EF233C' : '#3B82F6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: 18,
                    overflow: 'hidden',
                    cursor: viewModal.image ? 'pointer' : 'default',
                    border: '1px solid rgba(0,0,0,0.08)',
                  }}
                  onClick={() => viewModal.image && setPreviewImage(viewModal)}
                  title={viewModal.image ? 'Click to view full photo' : ''}
                >
                  {viewModal.image ? (
                    <img src={viewModal.image} alt={viewModal.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : (
                    viewModal.name?.charAt(0) || 'A'
                  )}
                </div>
                <div>
                  <h3 style={{ margin: 0 }}>{viewModal.name}</h3>
                  <span className="text-muted" style={{ fontSize: 12 }}>{viewModal.email} · {viewModal.role?.toUpperCase()}</span>
                </div>
              </div>
              <button className="modal-close" onClick={() => setViewModal(null)}><MdClose /></button>
            </div>
            <div className="modal-body" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
              {statsLoading && !reporterStats ? (
                <div className="text-center py-md">Loading stats...</div>
              ) : (
                <>
                  <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px', marginBottom: '20px', display: 'grid' }}>
                    <div className="stat-card" style={{ padding: '15px', background: '#DC262615', color: '#DC2626', borderRadius: '8px', border: '1px solid #DC262620' }}>
                      <div className="stat-info">
                        <h3 style={{ fontSize: '24px', margin: '0 0 5px 0' }}>{reporterStats?.totalArticles || 0}</h3>
                        <p style={{ margin: 0, fontSize: '13px', fontWeight: 500 }}>Total Articles</p>
                      </div>
                    </div>
                    <div className="stat-card" style={{ padding: '15px', background: '#F59E0B15', color: '#F59E0B', borderRadius: '8px', border: '1px solid #F59E0B20' }}>
                      <div className="stat-info">
                        <h3 style={{ fontSize: '24px', margin: '0 0 5px 0' }}>{reporterStats?.articlesToday || 0}</h3>
                        <p style={{ margin: 0, fontSize: '13px', fontWeight: 500 }}>Articles Today</p>
                      </div>
                    </div>
                  </div>

                  <h3 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '10px' }}>Articles Created Summary</h3>

                  {statsLoading ? (
                    <div className="text-center py-md">Loading summary...</div>
                  ) : (
                    <>
                      <div className="table-wrap">
                        <table className="table">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Article Count</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reporterArticles.map(day => (
                              <tr key={day.time}>
                                <td style={{ fontWeight: 500 }}>{day.dateStr}</td>
                                <td>{day.count}</td>
                              </tr>
                            ))}
                            {reporterArticles.length === 0 && (
                              <tr>
                                <td colSpan="2" className="empty-row" style={{ textAlign: 'center', padding: '25px', color: '#666' }}>
                                  No articles found for this reporter.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      <div style={{ marginTop: '15px', display: 'flex', justifyContent: 'center' }}>
                        <Pagination pagination={articlesPagination} onPageChange={handlePageChange} limit={reporterArticlesLimit} onLimitChange={handleReporterLimitChange} />
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
            <div className="modal-footer" style={{ justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={() => setViewModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div className="modal-overlay" onClick={() => setDeleteModal(null)}>
          <div className="modal modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Delete Account</h3>
              <button className="modal-close" onClick={() => setDeleteModal(null)}><MdClose /></button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this account?</p>
              <p className="text-muted" style={{ marginTop: 8 }}>{deleteModal.name} ({deleteModal.email})</p>
              <p className="text-muted" style={{ marginTop: 8 }}>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setDeleteModal(null)}>Cancel</button>
              <button className="btn btn-danger-solid" onClick={confirmDelete} disabled={deletingId === deleteModal.id}>
                {deletingId === deleteModal.id ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Full Image Preview Lightbox Modal */}
      {previewImage && (
        <div 
          className="modal-overlay" 
          style={{ zIndex: 9999, background: 'rgba(0, 0, 0, 0.78)', backdropFilter: 'blur(5px)' }} 
          onClick={() => setPreviewImage(null)}
        >
          <div 
            className="modal" 
            style={{ maxWidth: 480, width: '92%', padding: 0, overflow: 'hidden', borderRadius: 20, background: 'var(--card)', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.45)' }} 
            onClick={e => e.stopPropagation()}
          >
            <div className="modal-header" style={{ padding: '16px 20px', borderBottom: '1px solid #E2E8F0', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--text)' }}>{previewImage.name}</h3>
                <span className="text-muted" style={{ fontSize: 12 }}>{previewImage.email} · {previewImage.role?.toUpperCase()}</span>
              </div>
              <button 
                className="modal-close" 
                onClick={() => setPreviewImage(null)} 
                style={{ width: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 8, background: 'var(--bg)', border: 'none', cursor: 'pointer' }}
              >
                <MdClose size={18} />
              </button>
            </div>
            <div className="modal-body" style={{ padding: 20, textAlign: 'center', background: 'var(--bg)' }}>
              <div style={{ width: '100%', maxHeight: 440, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', borderRadius: 14, background: '#0F172A', boxShadow: '0 4px 14px rgba(0,0,0,0.1)' }}>
                <img
                  src={previewImage.image || previewImage.url}
                  alt={previewImage.name}
                  style={{ width: '100%', maxHeight: 440, objectFit: 'contain', display: 'block' }}
                />
              </div>
            </div>
            <div className="modal-footer" style={{ padding: '12px 20px', borderTop: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <a
                href={previewImage.image || previewImage.url}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-sm btn-ghost"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12 }}
              >
                Open Original ↗
              </a>
              <button className="btn btn-sm btn-primary" onClick={() => setPreviewImage(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
